import React, { useMemo, useState } from 'react';
import { History, StickyNote, Loader2, MapPin } from 'lucide-react';
import { Button } from '../../ui';
import { Textarea, ToggleGroup, ToggleItem } from '../../ui-v2.5';
import Timeline from './Timeline';
import { NOTA_MAX, AyudaNota } from './NotaDeHistorial';

/**
 * HistorialActivo — la pestaña Historial del Drawer de un activo (19/10, Fase 2).
 *
 *   · Arriba, la caja para escribir una NOTA (sólo con can_edit sobre el tipo: `puedeEditar`).
 *     El servidor decide igual; esto sólo oculta.
 *   · Debajo, el filtro Todo / Notas / Ubicación. «Ubicación» (Fase 3) sólo sale si el tipo tiene
 *     ubicación o el activo ya tiene movimientos: un filtro que nunca muestra nada parece roto.
 *   · Y el Timeline, una sola línea de tiempo con todo.
 *
 * Una nota se envía SIN fecha (la pone el servidor: hoy en Madrid) y sin persona: su autor es
 * quien la escribe (`created_by`).
 */

// El límite (5000) es el del servidor y vive en NotaDeHistorial, que lo usa también al editar.
// `maxLength` del <textarea> y el servidor cuentan igual (unidades UTF-16 de JS).

export default function HistorialActivo({
  eventos, puedeEditar, onAnadirNota, onEditarFecha, onEliminar, onEditarNota, onBorrarNota,
  tieneUbicacion = false,
}) {
  const [texto, setTexto] = useState('');
  const [filtro, setFiltro] = useState('todo');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const lista = useMemo(() => (Array.isArray(eventos) ? eventos : []), [eventos]);
  const notas = useMemo(() => lista.filter((e) => e.event_type === 'nota'), [lista]);
  const movimientos = useMemo(() => lista.filter((e) => e.event_type === 'movimiento'), [lista]);
  const conUbicacion = tieneUbicacion || movimientos.length > 0;
  const visibles = filtro === 'notas' ? notas : filtro === 'ubicacion' ? movimientos : lista;
  const limpio = texto.trim();

  async function enviar() {
    if (!limpio || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await onAnadirNota(limpio);
      setTexto('');
      setFiltro('todo');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'No se pudo guardar la nota.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-4">
      {puedeEditar && (
        <div>
          <Textarea
            label="Nueva nota"
            rows={3}
            maxLength={NOTA_MAX}
            value={texto}
            onChange={(e) => { setTexto(e.target.value); if (error) setError(null); }}
            placeholder="Escribe una nota para el historial de este activo…"
            hint={`${texto.length.toLocaleString('es-ES')} / ${NOTA_MAX.toLocaleString('es-ES')}`}
            error={error}
            disabled={enviando}
          />
          <AyudaNota />
          <div className="mt-2 flex justify-end">
            <Button onClick={enviar} disabled={!limpio || enviando} className="w-full sm:w-auto">
              {enviando
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <StickyNote className="w-4 h-4 mr-1.5" />}
              Añadir nota
            </Button>
          </div>
        </div>
      )}

      <ToggleGroup value={filtro} onChange={setFiltro}>
        <ToggleItem value="todo" label="Todo" icon={History} count={lista.length} />
        <ToggleItem value="notas" label="Notas" icon={StickyNote} count={notas.length} />
        {conUbicacion && <ToggleItem value="ubicacion" label="Ubicación" icon={MapPin} count={movimientos.length} />}
      </ToggleGroup>

      {filtro === 'notas' && notas.length === 0 ? (
        <p className="text-sm text-slate-500">Todavía no hay notas en este activo.</p>
      ) : filtro === 'ubicacion' && movimientos.length === 0 ? (
        <p className="text-sm text-slate-500">Todavía no hay movimientos en este activo.</p>
      ) : (
        <Timeline
          eventos={visibles}
          onEditarFecha={onEditarFecha}
          onEliminar={onEliminar}
          onEditarNota={onEditarNota}
          onBorrarNota={onBorrarNota}
        />
      )}
    </div>
  );
}
